import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories, siteConfig } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleOutsideTap = (e: MouseEvent | TouchEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideTap);
    document.addEventListener("touchstart", handleOutsideTap);
    return () => {
      document.removeEventListener("mousedown", handleOutsideTap);
      document.removeEventListener("touchstart", handleOutsideTap);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="text-xl font-bold font-[family-name:var(--font-heading)] text-foreground-950">
              GTBuy
            </span>
            <span className="hidden sm:inline text-sm text-foreground-600 font-[family-name:var(--font-body)]">
              Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={siteConfig.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              KASpreadsheet
            </a>

            <Link
              to="/article/gtbuy-spreadsheet-guide"
              className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              Guide
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={siteConfig.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              gtbuk sheet
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <i className={`text-xl text-foreground-950 ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-16 bg-black/30 z-40"
            onClick={closeMobileMenu}
          />
          <div
            ref={mobileMenuRef}
            className="md:hidden fixed top-16 left-0 right-0 bottom-0 bg-background-50 z-50 overflow-y-auto"
          >
            <div className="flex flex-col gap-1 pt-2 px-4 pb-8">
              <Link
                to="/"
                className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer"
                onClick={closeMobileMenu}
              >
                Home
              </Link>
              <Link
                to="/article/gtbuy-spreadsheet-guide"
                className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer"
                onClick={closeMobileMenu}
              >
                Guide
              </Link>

              <div className="px-4 pt-3 pb-1.5 text-xs font-medium text-foreground-400 uppercase tracking-wider">
                Categories
              </div>

              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer"
                >
                  {cat.name}
                </a>
              ))}

              <a
                href={siteConfig.mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-md text-center mt-3 cursor-pointer"
              >
                gtbuk sheet
              </a>

              <a
                href={siteConfig.mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-3 text-sm font-medium border border-primary-500 text-primary-500 rounded-md text-center mt-1 cursor-pointer"
              >
                buy it now
              </a>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}